import React, { useEffect, useMemo, useState } from 'react';
import { Box, Grid, Card, CardContent, Typography, List, ListItem, ListItemText, TextField, Button, Stack, Chip } from '@mui/material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiService } from '../../services/apiService';

interface BillKey { congress: number; type: string; number: string; }

function parseBillIdent(s: string | null): BillKey | null {
  if (!s) return null;
  const parts = s.split('/');
  if (parts.length !== 3) return null;
  const congress = parseInt(parts[0]);
  const type = parts[1];
  const number = parts[2];
  if (!congress || !type || !number) return null;
  return { congress, type, number };
}

const CompareBills: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const aParam = params.get('a');
  const bParam = params.get('b');
  const idsParam = params.get('ids');

  const [aKey, setAKey] = useState<string>(aParam || '');
  const [bKey, setBKey] = useState<string>(bParam || '');
  const [idsInput, setIdsInput] = useState<string>(idsParam || '');

  const a = useMemo(() => parseBillIdent(aParam), [aParam]);
  const b = useMemo(() => parseBillIdent(bParam), [bParam]);

  const [aData, setAData] = useState<any | null>(null);
  const [bData, setBData] = useState<any | null>(null);
  const [compareItems, setCompareItems] = useState<any[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const loadDetail = async (k: 'a'|'b', key: BillKey | null) => {
    if (!key) return;
    try {
      const resp = await apiService.getBillDetail(key.congress, key.type, key.number);
      if (k === 'a') setAData(resp.data);
      else setBData(resp.data);
    } catch {
      if (k === 'a') setAData(null); else setBData(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setError(null);
      setLoading(true);
      setCompareItems(null);
      try {
        if (idsParam && idsParam.trim()) {
          const ids = idsParam.split(',').map(s => s.trim()).filter(Boolean);
          if (ids.length) {
            const resp = await apiService.compareBillsByIds(ids);
            if (!cancelled) setCompareItems(Array.isArray(resp?.data?.items) ? resp.data.items : []);
          }
        } else {
          await Promise.all([
            loadDetail('a', a),
            loadDetail('b', b),
          ]);
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load comparison');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aParam, bParam, idsParam]);

  const onApplyAB = () => {
    const usp = new URLSearchParams();
    if (aKey) usp.set('a', aKey.trim());
    if (bKey) usp.set('b', bKey.trim());
    navigate({ pathname: '/compare/bills', search: `?${usp.toString()}` });
  };

  const onApplyIds = () => {
    const usp = new URLSearchParams();
    if (idsInput.trim()) usp.set('ids', idsInput.trim());
    navigate({ pathname: '/compare/bills', search: `?${usp.toString()}` });
  };

  const BillCard: React.FC<{ data: any | null, label?: string }> = ({ data, label }) => (
    <Card>
      <CardContent>
        {label && <Typography variant="h6" gutterBottom>{label}</Typography>}
        {!data ? (
          <Typography variant="body2" color="textSecondary">No data</Typography>
        ) : (
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              {data.title || `${(data.bill_type||data.type||'').toUpperCase()} ${data.bill_number||data.number}`}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Latest: {data.latest_action_text || data.latestAction?.text || ''}
            </Typography>
            {/* Party mix (when coming from /compare/bills) */}
            {data.cosponsorsByParty && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="textSecondary">Cosponsors by party:</Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                  {Object.entries(data.cosponsorsByParty).map(([party, count]: any, idx: number) => (
                    <Chip key={idx} size="small" label={`${party}: ${count}`} />
                  ))}
                </Stack>
              </Box>
            )}
            <List>
              {Array.isArray(data.sponsors) && data.sponsors.length > 0 && (
                <ListItem>
                  <ListItemText primary={`Sponsors: ${data.sponsors.map((s: any) => s.name || `${s.firstName||''} ${s.lastName||''}`.trim()).join(', ')}`}/>
                </ListItem>
              )}
              {Array.isArray(data.cosponsors) && data.cosponsors.length > 0 && (
                <ListItem>
                  <ListItemText primary={`Cosponsors: ${data.cosponsors.length}`}/>
                </ListItem>
              )}
              {data.summary && (
                <ListItem>
                  <ListItemText primary="Official summary" secondary={new DOMParser().parseFromString(data.summary, 'text/html').body.textContent || ''}/>
                </ListItem>
              )}
            </List>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const idsMode = Boolean(idsParam && idsParam.trim());

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Compare Bills</Typography>

      {/* IDs mode input */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField label="Bill IDs (comma-separated: 119/hr/1,119/s/5)" value={idsInput} onChange={e => setIdsInput(e.target.value)} size="small" fullWidth />
        <Button variant="contained" onClick={onApplyIds}>Compare via API</Button>
      </Stack>

      {/* A/B fallback */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField label="Bill A (e.g., 119/hr/1)" value={aKey} onChange={e => setAKey(e.target.value)} size="small" />
        <TextField label="Bill B (e.g., 119/s/5)" value={bKey} onChange={e => setBKey(e.target.value)} size="small" />
        <Button variant="outlined" onClick={onApplyAB}>Compare A vs B</Button>
      </Stack>

      {error && <Typography color="error" variant="body2">{error}</Typography>}
      {loading && <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>Loading…</Typography>}

      {idsMode ? (
        <Grid container spacing={2}>
          {(compareItems || []).map((item: any, idx: number) => (
            <Grid item xs={12} md={6} lg={4} key={idx}>
              <BillCard data={item} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <BillCard data={aData} label="Bill A" />
          </Grid>
          <Grid item xs={12} md={6}>
            <BillCard data={bData} label="Bill B" />
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default CompareBills;
